///
/// EarthBrowser
///
/// EBWX Binary Weather Grid Decoder
///
/// Decodes int16-quantized weather grid shards in the EBWX format.
/// Handles both global current grids and regional forecast shards.
///
/// Created by Matt Giger on 4/12/2026
/// Copyright (c) 2026 Geodata Labs
///

const INT16_NODATA = -32768

/**
 * Variables holding category codes rather than quantities. Interpolating
 * these is meaningless (rain=1 blended with snow=5 would read as freezing
 * rain=3), so point queries take the nearest grid cell instead.
 */
export const CATEGORICAL_VARIABLES: ReadonlySet<string> = new Set(["ptype"])

export interface EBWXVariable {
	name: string
	scale: number
	offset: number
}

export interface EBWXTimestep {
	validTime: number
	forecastHour: number
}

export interface EBWXHeader {
	version: number
	tier: number
	resolution: number
	west: number
	south: number
	nx: number
	ny: number
	nTimesteps: number
	nVariables: number
	variables: EBWXVariable[]
	timesteps: EBWXTimestep[]
	headerSize: number
}

export interface EBWXGridPoint {
	lat: number
	lon: number
	validTime: number
	forecastHour: number
	values: Record<string, number | null>
}

export function decodeEBWXHeader(buffer: ArrayBuffer): EBWXHeader {
	const view = new DataView(buffer)
	let offset = 0

	const magic = String.fromCharCode(
		view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)
	)
	if (magic !== 'EBWX') {
		throw new Error(`Invalid EBWX magic: ${magic}`)
	}
	offset = 4

	const version = view.getUint8(offset); offset += 1
	const tier = view.getUint8(offset); offset += 1
	const resolution = view.getFloat32(offset, true); offset += 4
	const west = view.getFloat32(offset, true); offset += 4
	const south = view.getFloat32(offset, true); offset += 4
	const nx = view.getUint16(offset, true); offset += 2
	const ny = view.getUint16(offset, true); offset += 2
	const nTimesteps = view.getUint16(offset, true); offset += 2
	const nVariables = view.getUint16(offset, true); offset += 2

	const variables: EBWXVariable[] = []
	for (let i = 0; i < nVariables; i++) {
		const nameBytes = new Uint8Array(buffer, offset, 16)
		const nullIdx = nameBytes.indexOf(0)
		const name = new TextDecoder().decode(nameBytes.subarray(0, nullIdx >= 0 ? nullIdx : 16))
		offset += 16
		const scale = view.getFloat32(offset, true); offset += 4
		const varOffset = view.getInt16(offset, true); offset += 2
		variables.push({ name, scale, offset: varOffset })
	}

	const timesteps: EBWXTimestep[] = []
	for (let i = 0; i < nTimesteps; i++) {
		const validTime = view.getUint32(offset, true); offset += 4
		const forecastHour = view.getUint16(offset, true); offset += 2
		timesteps.push({ validTime, forecastHour })
	}

	return {
		version, tier, resolution, west, south,
		nx, ny, nTimesteps, nVariables,
		variables, timesteps,
		headerSize: offset,
	}
}

export function decodeEBWXData(buffer: ArrayBuffer, header: EBWXHeader): EBWXGridPoint[] {
	const { nx, ny, nTimesteps, nVariables, variables, timesteps, headerSize, resolution, west, south } = header
	const dataView = new DataView(buffer)
	const north = south + ny * resolution
	const points: EBWXGridPoint[] = []

	let byteOffset = headerSize

	for (let t = 0; t < nTimesteps; t++) {
		const ts = timesteps[t]
		const varArrays: (Int16Array)[] = []

		for (let v = 0; v < nVariables; v++) {
			const plane = new Int16Array(ny * nx)
			for (let i = 0; i < ny * nx; i++) {
				plane[i] = dataView.getInt16(byteOffset, true)
				byteOffset += 2
			}
			varArrays.push(plane)
		}

		for (let row = 0; row < ny; row++) {
			for (let col = 0; col < nx; col++) {
				const lat = north - row * resolution
				const lon = west + col * resolution
				const idx = row * nx + col

				const values: Record<string, number | null> = {}
				let hasAnyValue = false

				for (let v = 0; v < nVariables; v++) {
					const raw = varArrays[v][idx]
					if (raw === INT16_NODATA) {
						values[variables[v].name] = null
					} else {
						values[variables[v].name] = raw * variables[v].scale
						hasAnyValue = true
					}
				}

				if (hasAnyValue) {
					points.push({
						lat, lon,
						validTime: ts.validTime,
						forecastHour: ts.forecastHour,
						values,
					})
				}
			}
		}
	}

	return points
}

export interface EBWXPlane {
	data: Float32Array
	nx: number
	ny: number
	west: number
	south: number
	resolution: number
	validTime: number
	forecastHour: number
}

export function decodeEBWXPlane(
	buffer: ArrayBuffer,
	header: EBWXHeader,
	variableName: string,
	timestepIndex: number
): EBWXPlane | null {
	const { nx, ny, nVariables, variables, timesteps, headerSize } = header

	const varIndex = variables.findIndex(v => v.name === variableName)
	if (varIndex < 0) return null
	if (timestepIndex < 0 || timestepIndex >= timesteps.length) return null

	const variable = variables[varIndex]
	const ts = timesteps[timestepIndex]
	const planeSize = ny * nx
	const planeByteOffset = headerSize + (timestepIndex * nVariables + varIndex) * planeSize * 2
	const dataView = new DataView(buffer)
	const data = new Float32Array(planeSize)

	for (let i = 0; i < planeSize; i++) {
		const raw = dataView.getInt16(planeByteOffset + i * 2, true)
		data[i] = raw === INT16_NODATA ? NaN : raw * variable.scale
	}

	return {
		data,
		nx,
		ny,
		west: header.west,
		south: header.south,
		resolution: header.resolution,
		validTime: ts.validTime,
		forecastHour: ts.forecastHour,
	}
}

export function decodeEBWXAtPoint(
	buffer: ArrayBuffer,
	header: EBWXHeader,
	lat: number,
	lon: number
): Array<{ validTime: number, forecastHour: number, values: Record<string, number | null> }> {
	const { nx, ny, nTimesteps, nVariables, variables, timesteps, headerSize, resolution, west, south } = header
	const north = south + (ny - 1) * resolution
	const dataView = new DataView(buffer)

	const row0 = Math.floor((north - lat) / resolution)
	const col0 = Math.floor((lon - west) / resolution)
	const row1 = Math.min(row0 + 1, ny - 1)
	const col1 = Math.min(col0 + 1, nx - 1)

	const latFrac = ((north - lat) / resolution) - row0
	const lonFrac = ((lon - west) / resolution) - col0

	const results: Array<{ validTime: number, forecastHour: number, values: Record<string, number | null> }> = []
	const planeSize = ny * nx * 2

	// Nearest cell for categorical variables
	const nearestRow = latFrac < 0.5 ? row0 : row1
	const nearestCol = lonFrac < 0.5 ? col0 : col1

	for (let t = 0; t < nTimesteps; t++) {
		const ts = timesteps[t]
		const values: Record<string, number | null> = {}

		for (let v = 0; v < nVariables; v++) {
			const planeOffset = headerSize + (t * nVariables + v) * planeSize

			if (CATEGORICAL_VARIABLES.has(variables[v].name)) {
				const q = dataView.getInt16(planeOffset + (nearestRow * nx + nearestCol) * 2, true)
				values[variables[v].name] = q === INT16_NODATA ? null : q * variables[v].scale
				continue
			}

			const q00 = dataView.getInt16(planeOffset + (row0 * nx + col0) * 2, true)
			const q01 = dataView.getInt16(planeOffset + (row0 * nx + col1) * 2, true)
			const q10 = dataView.getInt16(planeOffset + (row1 * nx + col0) * 2, true)
			const q11 = dataView.getInt16(planeOffset + (row1 * nx + col1) * 2, true)

			if (q00 === INT16_NODATA || q01 === INT16_NODATA || q10 === INT16_NODATA || q11 === INT16_NODATA) {
				values[variables[v].name] = null
			} else {
				const top = q00 + (q01 - q00) * lonFrac
				const bot = q10 + (q11 - q10) * lonFrac
				const interpolated = top + (bot - top) * latFrac
				values[variables[v].name] = interpolated * variables[v].scale
			}
		}

		results.push({ validTime: ts.validTime, forecastHour: ts.forecastHour, values })
	}

	return results
}
